/**
 * ── Mega-menu hover race-condition fix ──────────────────────────────────────
 * Problem: When the user quickly moves the cursor away from a mega-menu and
 * then moves it back, the theme's 400 ms close animation fires its .then()
 * callback and calls removeAttribute('open') on the <details> element — even
 * though the user has already re-hovered (aria-expanded is already "true"
 * again). This makes the menu disappear right after it tries to open.
 *
 * Fix: Intercept removeAttribute() on header__menu-disclosure <details>
 * elements and block the removal of 'open' if aria-expanded is still "true".
 * -----------------------------------------------------------------------------
 */
(function fixMegaMenuHoverRace() {
  const original = Element.prototype.removeAttribute;
  Element.prototype.removeAttribute = function (name) {
    if (
      name === 'open' &&
      this.tagName === 'DETAILS' &&
      this.classList && this.classList.contains('header__menu-disclosure') &&
      this.getAttribute('aria-expanded') === 'true'
    ) {
      // Re-hover happened during close animation — keep the menu open
      return;
    }
    return original.call(this, name);
  };
})();

function initializeStarterSections() {
  document.querySelectorAll("[data-starter-card]").forEach((card) => {
    if (card.dataset.initialized === "true") return;

    const heading = card.dataset.heading || "";
    const text = card.dataset.text || "";
    const buttonLabel = card.dataset.buttonLabel || "";

    if (heading || text || buttonLabel) {
      card.classList.add("is-highlighted");
    }

    card.dataset.initialized = "true";
  });
}

function initializeCollectionCarousels() {
  document.querySelectorAll("[data-carousel]").forEach((carousel) => {
    if (carousel.dataset.initialized === "true") return;

    const track = carousel.querySelector("[data-carousel-track]");
    const previousButton = carousel.querySelector("[data-carousel-previous]");
    const nextButton = carousel.querySelector("[data-carousel-next]");

    if (!track || !previousButton || !nextButton) return;

    const getScrollAmount = () => {
      const firstCard = track.firstElementChild;

      if (!firstCard) return track.clientWidth;

      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || "0");

      return firstCard.getBoundingClientRect().width + gap;
    };

    const updateNavigationState = () => {
      const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
      const canScroll = maxScrollLeft > 4;
      const atStart = track.scrollLeft <= 4;
      const atEnd = track.scrollLeft >= maxScrollLeft - 4;

      previousButton.hidden = !canScroll || atStart;
      nextButton.hidden = !canScroll || atEnd;
      previousButton.disabled = !canScroll || atStart;
      nextButton.disabled = !canScroll || atEnd;
    };

    previousButton.addEventListener("click", () => {
      track.scrollBy({ left: -getScrollAmount(), behavior: "smooth" });
    });

    nextButton.addEventListener("click", () => {
      track.scrollBy({ left: getScrollAmount(), behavior: "smooth" });
    });

    const autoplaySeconds = Number.parseFloat(carousel.getAttribute("data-carousel-autoplay") || "0");
    if (autoplaySeconds > 0) {
      let autoplayHandle = null;
      let isHovered = false;

      const startAutoplay = () => {
        if (isHovered || autoplayHandle) return;
        autoplayHandle = window.setInterval(() => {
          const maxScrollLeft = track.scrollWidth - track.clientWidth;
          const nextScrollLeft = track.scrollLeft + getScrollAmount();

          if (nextScrollLeft >= maxScrollLeft - 4) {
            track.scrollTo({ left: 0, behavior: "smooth" });
          } else {
            track.scrollBy({ left: getScrollAmount(), behavior: "smooth" });
          }
        }, autoplaySeconds * 1000);
      };

      const stopAutoplay = () => {
        if (autoplayHandle) {
          window.clearInterval(autoplayHandle);
          autoplayHandle = null;
        }
      };

      // Start/stop autoplay only when carousel is visible in viewport
      new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            startAutoplay();
          } else {
            stopAutoplay();
          }
        },
        { threshold: 0.1 }
      ).observe(carousel);

      carousel.addEventListener("pointerenter", () => { isHovered = true; stopAutoplay(); });
      carousel.addEventListener("pointerleave", () => { isHovered = false; startAutoplay(); });
    }

    track.addEventListener("scroll", updateNavigationState, { passive: true });
    window.addEventListener("resize", updateNavigationState);

    updateNavigationState();
    carousel.dataset.initialized = "true";
  });
}

function initializeResponsiveSlideshows() {
  document.querySelectorAll("[data-slideshow]").forEach((slideshow) => {
    if (slideshow.dataset.initialized === "true") return;

    const track = slideshow.querySelector("[data-slideshow-track]");
    const previousButton = slideshow.querySelector("[data-slideshow-previous]");
    const nextButton = slideshow.querySelector("[data-slideshow-next]");
    const dotButtons = Array.from(slideshow.querySelectorAll("[data-slideshow-dot]"));

    if (!track || !previousButton || !nextButton) return;

    const slides = Array.from(track.children);
    if (!slides.length) return;

    let currentIndex = 0;
    let autoplayHandle = null;
    let pointerStartX = 0;
    let pointerDeltaX = 0;
    let isDragging = false;

    const render = () => {
      track.style.transform = `translateX(-${currentIndex * 100}%)`;
      previousButton.disabled = slides.length <= 1;
      nextButton.disabled = slides.length <= 1;
      dotButtons.forEach((dotButton, index) => {
        dotButton.classList.toggle("is-active", index === currentIndex);
      });
    };

    const goTo = (index) => {
      currentIndex = (index + slides.length) % slides.length;
      render();
    };

    previousButton.addEventListener("click", () => {
      goTo(currentIndex - 1);
    });

    nextButton.addEventListener("click", () => {
      goTo(currentIndex + 1);
    });

    dotButtons.forEach((dotButton) => {
      dotButton.addEventListener("click", () => {
        const index = Number.parseInt(dotButton.getAttribute("data-slideshow-index") || "0", 10);
        goTo(index);
      });
    });

    const autoplaySeconds = Number.parseFloat(slideshow.getAttribute("data-slideshow-autoplay") || "0");
    let isVisible = false;

    const startAutoplay = () => {
      if (autoplaySeconds <= 0 || slides.length <= 1 || autoplayHandle || !isVisible) return;

      autoplayHandle = window.setInterval(() => {
        goTo(currentIndex + 1);
      }, autoplaySeconds * 1000);
    };

    const stopAutoplay = () => {
      if (!autoplayHandle) return;
      window.clearInterval(autoplayHandle);
      autoplayHandle = null;
    };

    // Start/stop autoplay only when slideshow is visible in viewport
    new IntersectionObserver(
      (entries) => {
        isVisible = entries[0].isIntersecting;
        if (isVisible) {
          startAutoplay();
        } else {
          stopAutoplay();
        }
      },
      { threshold: 0.1 }
    ).observe(slideshow);

    const onPointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;

      pointerStartX = event.clientX;
      pointerDeltaX = 0;
      isDragging = true;
      stopAutoplay();
    };

    const onPointerMove = (event) => {
      if (!isDragging) return;
      pointerDeltaX = event.clientX - pointerStartX;
    };

    const onPointerUp = () => {
      if (!isDragging) return;

      const swipeThreshold = Math.max(40, slideshow.clientWidth * 0.08);

      if (pointerDeltaX <= -swipeThreshold) {
        goTo(currentIndex + 1);
      } else if (pointerDeltaX >= swipeThreshold) {
        goTo(currentIndex - 1);
      } else {
        render();
      }

      isDragging = false;
      pointerDeltaX = 0;
      startAutoplay();
    };

    slideshow.addEventListener("pointerenter", stopAutoplay);
    slideshow.addEventListener("pointerleave", startAutoplay);
    track.addEventListener("pointerdown", onPointerDown);
    track.addEventListener("pointermove", onPointerMove);
    track.addEventListener("pointerup", onPointerUp);
    track.addEventListener("pointercancel", onPointerUp);
    track.addEventListener("pointerleave", onPointerUp);

    render();
    slideshow.dataset.initialized = "true";
  });
}

function initializeFocusMarquees() {
  document.querySelectorAll("[data-focus-marquee]").forEach((marquee) => {
    if (marquee.dataset.initialized === "true") return;

    const track = marquee.querySelector("[data-focus-marquee-track]");
    const baseRow = marquee.querySelector("[data-focus-marquee-row]");
    if (!track || !baseRow) return;

    const speedSeconds = Number.parseFloat(marquee.getAttribute("data-focus-marquee-speed") || "28");
    let cycleWidth = 0;
    let pxPerFrame = 0.2;

    const getGap = () => {
      const styles = window.getComputedStyle(track);
      return Number.parseFloat(styles.columnGap || styles.gap || "0");
    };

    const measure = () => {
      cycleWidth = baseRow.getBoundingClientRect().width + getGap();
      pxPerFrame = Math.max(0.2, cycleWidth / Math.max(speedSeconds * 60, 1));
    };

    const setupRows = () => {
      track.querySelectorAll("[data-focus-marquee-clone='true']").forEach((clone) => clone.remove());

      measure();

      let totalWidth = cycleWidth;
      let cloneCount = 0;

      while (cloneCount < 2 || totalWidth < marquee.clientWidth + cycleWidth * 2) {
        const clone = baseRow.cloneNode(true);
        clone.setAttribute("data-focus-marquee-clone", "true");
        clone.setAttribute("aria-hidden", "true");
        track.appendChild(clone);
        totalWidth += cycleWidth;
        cloneCount += 1;
      }
    };

    setupRows();

    let offset = 0;
    let paused = false;
    let frameId = 0;

    const tick = () => {
      if (!paused) {
        offset += pxPerFrame;

        if (offset >= cycleWidth) {
          offset -= cycleWidth;
        }

        track.style.transform = `translate3d(-${offset}px, 0, 0)`;
      }

      frameId = window.requestAnimationFrame(tick);
    };

    const startRaf = () => {
      if (!frameId) frameId = window.requestAnimationFrame(tick);
    };

    const stopRaf = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
        frameId = 0;
      }
    };

    // Completely stop RAF when marquee is off-screen — saves CPU/battery
    new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          startRaf();
        } else {
          stopRaf();
        }
      },
      { threshold: 0 }
    ).observe(marquee);

    const pauseOnHover = track.classList.contains("focus-marquee__track--pauseable");
    if (pauseOnHover) {
      marquee.addEventListener("pointerenter", () => {
        paused = true;
        track.classList.add("is-paused");
      });

      marquee.addEventListener("pointerleave", () => {
        paused = false;
        track.classList.remove("is-paused");
      });
    }

    const refresh = () => {
      offset = 0;
      track.style.transform = "translate3d(0, 0, 0)";
      setupRows();
    };

    window.addEventListener("resize", refresh);
    window.addEventListener("load", refresh);

    marquee.querySelectorAll("img").forEach((image) => {
      image.addEventListener("load", refresh, { once: true });
    });

    marquee.dataset.initialized = "true";
  });
}

function parseProductGalleryImages(value) {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (error) {
    return [];
  }
}

function renderProductGallery(card, images, nextIndex) {
  if (!card) return;

  const gallery = card.querySelector("[data-product-gallery]");
  const imageNode = card.querySelector("[data-product-image]");
  const dotsWrap = card.querySelector("[data-product-gallery-dots]");
  const dotButtons = Array.from(card.querySelectorAll("[data-product-gallery-dot]"));

  if (!gallery || !imageNode) return;

  const safeImages = Array.isArray(images) && images.length ? images : [imageNode.getAttribute("src")].filter(Boolean);
  const normalizedIndex = safeImages.length ? ((nextIndex % safeImages.length) + safeImages.length) % safeImages.length : 0;
  const activeImage = safeImages[normalizedIndex] || "";

  gallery.dataset.productGalleryImages = JSON.stringify(safeImages);
  gallery.dataset.productGalleryIndex = String(normalizedIndex);

  if (activeImage) {
    imageNode.setAttribute("src", activeImage);
    imageNode.setAttribute("srcset", activeImage);
  }

  if (dotsWrap) {
    const hasMultipleImages = safeImages.length > 1;
    dotsWrap.hidden = !hasMultipleImages;

    let startIndex = 0;
    if (safeImages.length > 3) {
      startIndex = Math.min(Math.max(normalizedIndex - 1, 0), safeImages.length - 3);
    }

    dotButtons.forEach((dotButton, dotIndex) => {
      const targetIndex = Math.min(startIndex + dotIndex, safeImages.length - 1);
      dotButton.dataset.targetIndex = String(targetIndex);
      dotButton.hidden = !hasMultipleImages || dotIndex >= Math.min(3, safeImages.length);
      dotButton.classList.toggle("is-active", targetIndex === normalizedIndex);
      dotButton.classList.toggle("is-edge", safeImages.length > 3 && dotIndex !== 1);
      dotButton.setAttribute("aria-label", `Show product image ${targetIndex + 1}`);
      dotButton.setAttribute("aria-pressed", String(targetIndex === normalizedIndex));
    });
  }
}

function initializeProductCardGalleries() {
  document.querySelectorAll(".products-carousel__card").forEach((card) => {
    if (card.dataset.galleryInitialized === "true") return;

    const gallery = card.querySelector("[data-product-gallery]");
    if (!gallery) return;

    const initialImages = parseProductGalleryImages(gallery.dataset.productGalleryImages);
    renderProductGallery(card, initialImages, Number.parseInt(gallery.dataset.productGalleryIndex || "0", 10) || 0);

    let pointerStartX = 0;
    let pointerDeltaX = 0;
    let isDragging = false;

    const stepGallery = (direction) => {
      const images = parseProductGalleryImages(gallery.dataset.productGalleryImages);
      const currentIndex = Number.parseInt(gallery.dataset.productGalleryIndex || "0", 10) || 0;
      renderProductGallery(card, images, currentIndex + direction);
    };

    card.querySelectorAll("[data-product-gallery-dot]").forEach((dotButton) => {
      dotButton.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const targetIndex = Number.parseInt(dotButton.dataset.targetIndex || "0", 10) || 0;
        const images = parseProductGalleryImages(gallery.dataset.productGalleryImages);
        renderProductGallery(card, images, targetIndex);
      });
    });

    gallery.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      pointerStartX = event.clientX;
      pointerDeltaX = 0;
      isDragging = true;
    });

    gallery.addEventListener("pointermove", (event) => {
      if (!isDragging) return;
      pointerDeltaX = event.clientX - pointerStartX;

      if (Math.abs(pointerDeltaX) > 6) {
        event.preventDefault();
      }
    });

    const onPointerEnd = () => {
      if (!isDragging) return;

      const swipeThreshold = Math.max(36, gallery.clientWidth * 0.08);

      if (pointerDeltaX <= -swipeThreshold) {
        stepGallery(1);
      } else if (pointerDeltaX >= swipeThreshold) {
        stepGallery(-1);
      }

      isDragging = false;
      pointerDeltaX = 0;
    };

    gallery.addEventListener("pointerup", onPointerEnd);
    gallery.addEventListener("pointercancel", onPointerEnd);
    gallery.addEventListener("pointerleave", onPointerEnd);

    card.dataset.galleryInitialized = "true";
  });
}

function initializeCustomSections() {
  initializeStarterSections();
  initializeCollectionCarousels();
  initializeResponsiveSlideshows();
  initializeFocusMarquees();
  initializeCollectionTabs();
  initializeProductCardGalleries();

  document.querySelectorAll("[data-linked-product-url]").forEach((button) => {
    if (button.dataset.initialized === "true") return;

    button.addEventListener("click", (event) => {
      event.preventDefault();

      const card = button.closest(".products-carousel__card");

      if (!card) {
        return;
      }

      card.querySelectorAll("[data-linked-product-url]").forEach((swatch) => {
        swatch.classList.toggle("is-active", swatch === button);
      });

      const url = button.getAttribute("data-linked-product-url") || "";
      const title = button.getAttribute("data-linked-product-title") || "";
      const shortTitle = button.getAttribute("data-linked-product-short-title") || title;
      const image = button.getAttribute("data-linked-product-image") || "";
      const images = parseProductGalleryImages(button.getAttribute("data-linked-product-images") || "");
      const price = button.getAttribute("data-linked-product-price") || "";
      const comparePrice = button.getAttribute("data-linked-product-compare-price") || "";
      const rating = button.getAttribute("data-linked-product-rating") || "";
      const reviewCount = button.getAttribute("data-linked-product-review-count") || "";
      const isAvailable = button.getAttribute("data-linked-product-available") === "true";
      const isBestSeller = button.getAttribute("data-linked-product-bestseller") === "true";
      const isTrending = button.getAttribute("data-linked-product-trending") === "true";
      const isNewArrivals = button.getAttribute("data-linked-product-new-arrivals") === "true";
      const discount = button.getAttribute("data-linked-product-discount") || "";
      const variantId = button.getAttribute("data-linked-product-variant-id") || "";

      card.querySelectorAll("[data-product-link]").forEach((link) => {
        link.setAttribute("href", url);
      });

      const imageNode = card.querySelector("[data-product-image]");
      if (imageNode && image) {
        imageNode.setAttribute("src", image);
        imageNode.setAttribute("srcset", image);
      }

      renderProductGallery(card, images.length ? images : [image].filter(Boolean), 0);

      const titleNode = card.querySelector("[data-product-title]");
      if (titleNode && title) {
        titleNode.textContent = shortTitle;
      }

      const priceNode = card.querySelector("[data-product-price]");
      if (priceNode) {
        priceNode.textContent = price;
      }

      const compareNode = card.querySelector("[data-product-compare-price]");
      if (compareNode) {
        if (comparePrice) {
          compareNode.textContent = comparePrice;
          compareNode.classList.remove("hidden");
        } else {
          compareNode.textContent = "";
          compareNode.classList.add("hidden");
        }
      }

      const buttonNode = card.querySelector("[data-product-button]");
      if (buttonNode) {
        const defaultLabel = buttonNode.getAttribute("data-default-button-label") || "Add to cart";
        const buttonText = buttonNode.querySelector(".products-carousel__button-text");
        if (buttonText) {
          buttonText.textContent = isAvailable ? defaultLabel : "Out of stock";
        }
        buttonNode.disabled = !isAvailable;
      }

      const variantInput = card.querySelector("[data-product-variant-id]");
      if (variantInput && variantId) {
        variantInput.value = variantId;
      }

      const reviewWrap = card.querySelector("[data-product-reviews]");
      if (reviewWrap) {
        if (rating) {
          reviewWrap.classList.remove("hidden");
          const ratingNode = card.querySelector("[data-product-rating]");
          const reviewCountNode = card.querySelector("[data-product-review-count]");

          if (ratingNode) ratingNode.textContent = rating;
          if (reviewCountNode) reviewCountNode.textContent = `(${reviewCount})`;
        } else {
          reviewWrap.classList.add("hidden");
        }
      }

      const badgesWrap = card.querySelector(".products-carousel__badges");
      if (badgesWrap) {
        const badges = [];
        if (isBestSeller) badges.push('<span class="products-carousel__badge products-carousel__badge--bestseller">Best Seller</span>');
        if (isTrending) badges.push('<span class="products-carousel__badge products-carousel__badge--trending">Trending</span>');
        if (isNewArrivals) badges.push('<span class="products-carousel__badge products-carousel__badge--new">New Arrivals</span>');
        if (discount) badges.push(`<span class="products-carousel__badge products-carousel__badge--discount"><span class="products-carousel__badge-discount-value">${discount}%</span><span class="products-carousel__badge-discount-label">Off</span></span>`);
        if (!isAvailable) badges.push('<span class="products-carousel__badge products-carousel__badge--stock">Sold Out</span>');
        badgesWrap.innerHTML = badges.join("");
      }
    });

    button.dataset.initialized = "true";
  });
}

function initializeCollectionTabs() {
  document.querySelectorAll("[data-collection-tabs]").forEach((tabsRoot) => {
    if (tabsRoot.dataset.initialized === "true") return;

    const buttons = Array.from(tabsRoot.querySelectorAll("[data-tab-button]"));
    const panels = Array.from(tabsRoot.querySelectorAll("[data-tab-panel]"));

    if (!buttons.length || !panels.length) return;

    const activateTab = (targetId) => {
      buttons.forEach((button) => {
        const isActive = button.getAttribute("data-tab-target") === targetId;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-selected", String(isActive));
      });

      panels.forEach((panel) => {
        const isActive = panel.id === targetId;
        panel.classList.toggle("is-active", isActive);
        panel.hidden = !isActive;
      });
    };

    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        activateTab(button.getAttribute("data-tab-target"));
      });
    });

    activateTab(buttons[0].getAttribute("data-tab-target"));
    tabsRoot.dataset.initialized = "true";
  });
}

document.addEventListener("DOMContentLoaded", initializeCustomSections);
document.addEventListener("shopify:section:load", initializeCustomSections);
window.addEventListener("load", initializeCustomSections);

// ── Quick view modal — Swiper gallery ─────────────────────────────────────
// dialog:before-show does NOT bubble (no bubbles:true in theme.js),
// so we attach listeners directly on each modal element.
// Swiper is lazy-loaded from CDN; preloaded during page idle for instant first open.

let _swiperReady = false;
let _swiperLoading = false;
const _swiperQueue = [];

function loadSwiperLib(cb) {
  if (_swiperReady) { cb(); return; }
  _swiperQueue.push(cb);
  if (_swiperLoading) return;
  _swiperLoading = true;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css";
  document.head.appendChild(link);

  const script = document.createElement("script");
  script.src = "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js";
  script.onload = function () {
    _swiperReady = true;
    _swiperQueue.forEach((fn) => fn());
    _swiperQueue.length = 0;
  };
  document.head.appendChild(script);
}

// Preload Swiper during idle — ONLY on collection/search pages where quick view is used heavily
// Product pages skip this to avoid loading 185KB CDN unnecessarily
window.addEventListener("load", function () {
  var isCollectionPage = !!document.querySelector('.shopify-section--main-collection, .shopify-section--main-search');
  if (!isCollectionPage) return;
  (window.requestIdleCallback || ((fn) => setTimeout(fn, 2000)))(function () {
    loadSwiperLib(function () {});
  });
});

function initModalSwiper(modal) {
  const el = modal.querySelector("[data-pcm-swiper]");
  if (!el || el._swiperInstance) return;

  const dotsWrap = modal.querySelector("[data-product-card-modal-gallery-dots]");

  const swiper = new window.Swiper(el, {
    slidesPerView: 1,
    speed: 380,
    grabCursor: true,
    watchOverflow: true,
    keyboard: { enabled: true },
    pagination: {
      el: el.querySelector(".pcm-swiper__dots"),
      clickable: true,
      dynamicBullets: true,
    },
    navigation: {
      prevEl: el.querySelector("[data-pcm-prev]"),
      nextEl: el.querySelector("[data-pcm-next]"),
    },
  });

  el._swiperInstance = swiper;

  // Watch hidden dots — theme.js empties + refills on swatch change
  if (dotsWrap) {
    let pending = false;
    const obs = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        const dotEls = Array.from(dotsWrap.querySelectorAll("[data-modal-gallery-dot]"));
        const imgs = dotEls.map((d) => d.getAttribute("data-image")).filter(Boolean);
        const activeIndex = dotEls.findIndex((d) => d.classList.contains("is-active"));
        if (imgs.length) rebuildSwiperSlides(swiper, imgs, activeIndex >= 0 ? activeIndex : 0);
      });
    });
    obs.observe(dotsWrap, { childList: true, subtree: false });
    el._swiperObserver = obs;
  }
}

function rebuildSwiperSlides(swiper, urls, activeIndex) {
  if (!swiper || !urls || !urls.length) return;
  const targetIndex = activeIndex || 0;
  try {
    swiper.removeAllSlides();
    const slideEls = urls.map((u) => {
      const div = document.createElement("div");
      div.className = "swiper-slide pcm-swiper__slide";
      const img = document.createElement("img");
      img.src = u;
      img.loading = "lazy";
      img.draggable = false;
      img.alt = "";
      div.appendChild(img);
      return div;
    });
    swiper.appendSlide(slideEls);
    swiper.slideTo(targetIndex, 0, false);
    swiper.update();
  } catch (err) {
    // Fallback: direct innerHTML manipulation
    const wrapper = swiper.el && swiper.el.querySelector(".swiper-wrapper");
    if (!wrapper) return;
    wrapper.innerHTML = urls
      .map((u) => `<div class="swiper-slide pcm-swiper__slide"><img src="${u}" loading="lazy" draggable="false" alt=""></div>`)
      .join("");
    swiper.update();
    swiper.slideTo(targetIndex, 0, false);
  }
}

function destroyModalSwiper(modal) {
  const el = modal.querySelector("[data-pcm-swiper]");
  if (!el) return;
  el._swiperObserver?.disconnect();
  el._swiperObserver = null;
  el._swiperInstance?.destroy(true, true);
  el._swiperInstance = null;
}

// Attach listeners directly on each modal element (event doesn't bubble)
function bindModalSwiperListeners(root) {
  (root || document).querySelectorAll(".product-card-variant-modal").forEach(function (modal) {
    if (modal._swiperListenerBound) return;
    modal._swiperListenerBound = true;

    modal.addEventListener("dialog:before-show", function () {
      loadSwiperLib(function () { initModalSwiper(modal); });
    });

    modal.addEventListener("dialog:after-hide", function () {
      destroyModalSwiper(modal);
    });

    // Swatch click → update Swiper gallery.
    // Attached directly on the modal so it fires even if x-modal
    // stops propagation before the event reaches document.
    modal.addEventListener("click", function (e) {
      var swatch = e.target.closest("[data-modal-swatch]");
      if (!swatch) return;

      // Let theme.js run first (updates title, price, gallery dots)
      setTimeout(function () {
        var el = modal.querySelector("[data-pcm-swiper]");
        if (!el) return;
        var swiper = el._swiperInstance || el.swiper;
        if (!swiper) return;

        var galleryStr = swatch.getAttribute("data-gallery") || "";
        var fallback   = swatch.getAttribute("data-image")   || "";
        var urls = galleryStr.split("||").map(function (s) { return s.trim(); }).filter(Boolean);
        if (!urls.length && fallback) urls = [fallback];
        if (!urls.length) return;

        var activeImage = swatch.getAttribute("data-image") || "";
        var activeIndex = activeImage ? urls.indexOf(activeImage) : 0;
        if (activeIndex < 0) activeIndex = 0;

        rebuildSwiperSlides(swiper, urls, activeIndex);
      }, 50);
    });
  });
}

document.addEventListener("DOMContentLoaded", function () { bindModalSwiperListeners(); });
document.addEventListener("shopify:section:load", function (e) { bindModalSwiperListeners(e.target); });
window.addEventListener("load", function () { bindModalSwiperListeners(); }); // safety net

// Re-bind after filter/page AJAX updates.
// theme:loading:end can fire before the new DOM is inserted, so we also
// use a MutationObserver to catch new modals the moment they appear.
document.documentElement.addEventListener("theme:loading:end", function () {
  bindModalSwiperListeners();
  // Retry after next two paints in case DOM wasn't ready yet
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      bindModalSwiperListeners();
    });
  });
});

// MutationObserver: bind listeners the instant new modal elements enter the DOM
(function () {
  if (!window.MutationObserver) return;
  var pending = false;
  var mo = new MutationObserver(function (mutations) {
    var hasNewModal = false;
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if ((n.classList && n.classList.contains("product-card-variant-modal")) ||
            (n.querySelector && n.querySelector(".product-card-variant-modal"))) {
          hasNewModal = true;
          break;
        }
      }
      if (hasNewModal) break;
    }
    if (!hasNewModal || pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      bindModalSwiperListeners();
    });
  });
  // Start observing once body exists
  function startObs() {
    if (document.body) {
      mo.observe(document.body, { childList: true, subtree: true });
    } else {
      document.addEventListener("DOMContentLoaded", function () {
        mo.observe(document.body, { childList: true, subtree: true });
      });
    }
  }
  startObs();
})();

// Fix: quick-buy-modal carousel arrows not working after AJAX filter/sort/page change.
//
// Root cause (theme.js lines 796-844):
//   carousel-prev/next-button has a `carousel` getter that runs document.getElementById
//   on EVERY click — not just once at setup time.  After AJAX the OLD quick-buy-modal
//   stays in document.body (same carousel ID, cleared but not removed).  getElementById
//   finds that hidden carousel first and select() fails silently → nothing slides.
//   The same silent failure can happen after pagination for other timing reasons.
//
// Fix A — capture-phase click interception (runs BEFORE the native broken handler):
//   A single permanent document listener intercepts every arrow click inside a
//   quick-buy-modal and calls carousel.previous() / .next() using a scoped
//   closest() lookup.  This always finds the carousel in the SAME modal, no matter
//   how many stale modals are lurking in body.
//
// Fix B — is-scrollable recalibration:
//   ResizeObserver may fire before the modal layout settles, leaving is-scrollable
//   off and the arrows invisible.  Re-check after dialog:after-show.
(function () {

  // ── PCM Swiper Fix ─────────────────────────────────────────────────────────
  // After AJAX filter/sort/pagination, the PCM (Product Card Modal) app's
  // Swiper.js navigation buttons lose their connection to the Swiper instance
  // because the DOM is replaced but Swiper keeps references to old elements.
  // Fix: intercept clicks on pcm-swiper__arrow buttons and drive the Swiper
  // instance directly via the .swiper property on the container element.

  function findSwiperContainer(btn) {
    // The arrow is usually a sibling of the .swiper container inside a wrapper
    var el = btn.parentElement;
    while (el && el !== document.body) {
      // Check if this element IS the swiper container
      if (el.classList && el.classList.contains("swiper") && el.swiper) {
        return el;
      }
      // Check direct children for a .swiper with a live instance
      if (el.querySelector) {
        var found = el.querySelector(".swiper");
        if (found && found.swiper) return found;
      }
      el = el.parentElement;
    }
    return null;
  }

  function manualSlide(btn, isNext) {
    // Fallback: manually translate the swiper-wrapper when no Swiper instance
    var el = btn.parentElement;
    var wrapper = null;
    while (el && el !== document.body) {
      wrapper = el.querySelector && el.querySelector(".swiper-wrapper");
      if (wrapper) break;
      el = el.parentElement;
    }
    if (!wrapper) return;

    var slides = Array.from(wrapper.querySelectorAll(".swiper-slide")).filter(function (s) {
      return !s.hasAttribute("hidden") && getComputedStyle(s).display !== "none";
    });
    if (slides.length <= 1) return;

    var active = wrapper.querySelector(".swiper-slide-active") || slides[0];
    var idx = slides.indexOf(active);
    var targetIdx = isNext ? Math.min(idx + 1, slides.length - 1) : Math.max(idx - 1, 0);
    if (targetIdx === idx) return;

    var target = slides[targetIdx];
    var containerEl = wrapper.parentElement;
    var offsetLeft = target.offsetLeft;

    // Update active class
    slides.forEach(function (s) { s.classList.remove("swiper-slide-active"); });
    target.classList.add("swiper-slide-active");

    // Animate
    wrapper.style.transition = "transform 0.3s ease";
    wrapper.style.transform = "translate3d(-" + offsetLeft + "px, 0, 0)";
  }

  // (Swatch → Swiper update is handled inside bindModalSwiperListeners
  //  directly on each modal element, so it fires even when x-modal
  //  stops click propagation before it reaches document.)

  // ── PCM arrow clicks ───────────────────────────────────────────────────────
  document.addEventListener("click", function (e) {
    var btn = e.target.closest(".pcm-swiper__arrow");
    if (!btn) return;

    var isNext = btn.classList.contains("pcm-swiper__arrow--next");
    var container = findSwiperContainer(btn);

    if (container && container.swiper) {
      // Swiper instance is alive — stop the stale built-in handler and drive directly
      e.stopImmediatePropagation();
      if (isNext) container.swiper.slideNext();
      else        container.swiper.slidePrev();
    } else {
      // Swiper instance missing — use manual slide fallback
      e.stopImmediatePropagation();
      manualSlide(btn, isNext);
    }
  }, true); // capture phase: runs before Swiper's own click handlers

})();
